import { defineMutator } from '@rocicorp/zero';
import { createPatch } from 'rfc6902';
import { uuidv7 } from 'uuidv7';
import * as z from 'zod';
import { CompoundMappingType, CompoundOrderStatus, CompoundCarrierType } from '../database/schema.js';
import { zql } from '../schema.js';
import { itemsDetail, statusItems } from '../util/event-log-items.js';
import { Status } from '../util/schemas.js';
import { assertCan, EventLogs } from '../utils.js';
import { DeleteResourceSchema, UpdateResourceStatusSchema, addEventLog, EventLogResources } from './util.js';
export const AddCompoundMappingSchema = z.object({
    mappingType: CompoundMappingType,
    sourceId: z.string().nullable().optional(),
    machineId: z.string().trim().nonempty(),
    carrierType: CompoundCarrierType.optional(),
    label: z.string().optional(),
    status: Status.optional(),
});
export const UpdateCompoundMappingSchema = AddCompoundMappingSchema.partial().extend({
    id: z.uuidv7().nonempty(),
});
async function findMappingById(tx, id) {
    return await tx.run(zql.compoundMappings.where('id', '=', id).where('deletedAt', 'IS', null).one());
}
/**
 * Resolves the effective carrierType for a compound mapping. Only CARRIER mappings may carry
 * a carrier type; for any other mapping type the current value is preserved unchanged.
 */
function resolveCarrierType(mappingType, current, input) {
    if (mappingType !== CompoundMappingType.enum.CARRIER)
        return current.carrierType;
    return input.carrierType !== undefined ? input.carrierType : current.carrierType;
}
export const compoundMappings = {
    add: defineMutator(AddCompoundMappingSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'interfaces.manage');
        const now = Date.now();
        const newMapping = {
            id: uuidv7(),
            mappingType: args.mappingType,
            sourceId: args.sourceId ?? null,
            machineId: args.machineId,
            carrierType: resolveCarrierType(args.mappingType, { carrierType: null }, { carrierType: args.carrierType }),
            label: args.label,
            status: args.status ?? Status.enum.active,
            createdAt: now,
            createdBy: ctx.user.id,
        };
        await tx.mutate.compoundMappings.insert(newMapping);
        if (tx.location === 'server') {
            const patches = createPatch({}, newMapping);
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.compoundMapping,
                resourceId: newMapping.id,
                action: 'create',
                status: 'OK',
                domain: 'configuration',
                event: EventLogs.COMPOUND_MAPPING_ADDED,
                details: {
                    mappingType: newMapping.mappingType,
                    machineId: newMapping.machineId,
                    id: newMapping.id,
                },
                patches,
                performedBy: ctx.user.id,
                performedAt: now,
            }, ctx);
        }
    }),
    update: defineMutator(UpdateCompoundMappingSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'interfaces.manage');
        const prev = await findMappingById(tx, args.id);
        if (!prev)
            throw new Error('Not Found');
        const now = Date.now();
        const mappingType = args.mappingType ?? prev.mappingType;
        const updatedMapping = {
            id: args.id,
            mappingType,
            sourceId: args.sourceId !== undefined ? (args.sourceId ?? null) : prev.sourceId,
            machineId: args.machineId ?? prev.machineId,
            carrierType: resolveCarrierType(mappingType, prev, { carrierType: args.carrierType }),
            label: args.label !== undefined ? args.label : prev.label,
            status: args.status,
            updatedAt: now,
            updatedBy: ctx.user.id,
        };
        await tx.mutate.compoundMappings.update(updatedMapping);
        if (tx.location === 'server') {
            const patches = createPatch(prev, updatedMapping);
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.compoundMapping,
                resourceId: args.id,
                action: 'update',
                status: 'OK',
                domain: 'configuration',
                event: EventLogs.COMPOUND_MAPPING_UPDATED,
                details: {
                    mappingType: updatedMapping.mappingType,
                    machineId: updatedMapping.machineId,
                    id: updatedMapping.id,
                },
                patches,
                performedBy: ctx.user.id,
                performedAt: now,
            }, ctx);
        }
    }),
    updateStatus: defineMutator(UpdateResourceStatusSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'interfaces.manage');
        const prev = await findMappingById(tx, args.id);
        if (!prev)
            throw new Error('Not found');
        const now = Date.now();
        const updatedMapping = {
            id: args.id,
            status: args.status,
            updatedAt: now,
            updatedBy: ctx.user.id,
        };
        await tx.mutate.compoundMappings.update(updatedMapping);
        if (tx.location === 'server') {
            const patches = createPatch({ status: prev.status }, { status: args.status });
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.compoundMapping,
                resourceId: args.id,
                action: 'update',
                patches,
                status: args.status === 'inactive' ? 'WARNING' : 'INFO',
                domain: 'configuration',
                event: EventLogs.COMPOUND_MAPPING_UPDATED,
                details: {
                    id: updatedMapping.id,
                    mappingType: prev.mappingType,
                    machineId: prev.machineId,
                    ...itemsDetail(statusItems(prev.status, args.status)),
                },
                performedBy: updatedMapping.updatedBy,
                performedAt: updatedMapping.updatedAt,
            }, ctx);
        }
    }),
    remove: defineMutator(DeleteResourceSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'interfaces.manage');
        const prev = await findMappingById(tx, args.id);
        if (!prev)
            throw new Error('Not found');
        const now = Date.now();
        const updatedMapping = {
            id: args.id,
            deletedAt: now,
            deletedBy: ctx.user.id,
        };
        await tx.mutate.compoundMappings.update(updatedMapping);
        if (tx.location === 'server') {
            const patches = createPatch(prev, updatedMapping);
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.compoundMapping,
                resourceId: args.id,
                action: 'delete',
                patches,
                status: 'WARNING',
                domain: 'configuration',
                event: EventLogs.COMPOUND_MAPPING_REMOVED,
                details: {
                    id: updatedMapping.id,
                    mappingType: prev.mappingType,
                    machineId: prev.machineId,
                },
                performedBy: updatedMapping.deletedBy,
                performedAt: updatedMapping.deletedAt,
            }, ctx);
        }
    }),
};
export const RetryCompoundOrderSchema = z.object({
    id: z.uuidv7(),
});
export const compoundOrders = {
    retry: defineMutator(RetryCompoundOrderSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'interfaces.manage');
        const prev = await tx.run(zql.compoundOrders.where('id', '=', args.id).where('deletedAt', 'IS', null).one());
        if (!prev)
            throw new Error('Not found');
        if (prev.status !== CompoundOrderStatus.enum.FAILED) {
            throw new Error('Only failed compound orders can be retried');
        }
        const now = Date.now();
        const updated = {
            id: args.id,
            status: CompoundOrderStatus.enum.PENDING,
            error: null,
            updatedAt: now,
            updatedBy: ctx.user.id,
        };
        await tx.mutate.compoundOrders.update(updated);
        if (tx.location === 'server') {
            const patches = createPatch({ status: prev.status, error: prev.error }, { status: updated.status, error: null });
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.compoundOrder,
                resourceId: args.id,
                action: 'update',
                patches,
                status: 'INFO',
                domain: 'production',
                event: EventLogs.COMPOUND_ORDER_GENERATED,
                details: {
                    externalId: prev.externalId,
                    id: args.id,
                },
                performedBy: updated.updatedBy,
                performedAt: updated.updatedAt,
            }, ctx);
        }
    }),
};
//# sourceMappingURL=compounders.mutators.js.map